import { CloseIcon, LanguageIcon, Logo, MenuIcon } from 'icons';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Add this for translations

export const Navbar: React.FC<{ hidden: boolean }> = ({ hidden }) => {
  const { t, i18n } = useTranslation(); // Hook for translations and language switching
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] =
    useState<boolean>(false);

  // Ref to detect clicks outside the dropdown
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobileMenuOpen]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };

    if (isLanguageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageDropdownOpen]);

  // Function to change language and close dropdown
  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLanguageDropdownOpen(false); // Close dropdown on selection
    setIsMobileMenuOpen(false); // Close mobile menu if open
  };

  return (
    <>
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 h-16 z-50 flex items-center px-smallPx md:px-mediumPx lg:px-largePx text-white transition-transform duration-300 ${
          hidden ? '-translate-y-full' : 'translate-y-0'
        }`}
      >
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black to-transparent opacity-100"></div>

        {/* Left Section (Logo) */}
        <button className="flex-1 relative" onClick={() => navigate('/')}>
          <Logo />
        </button>

        {/* Center Section (Menu Items for Desktop) */}
        <div className="flex-1 hidden md:flex justify-center space-x-6 relative">
          <Link to="/" className="text-white hover:text-gray-300">
            {t('navbar.home')}
          </Link>
          <Link to="/about" className="text-white hover:text-gray-300">
            {t('navbar.about')}
          </Link>
          <Link to="/contact" className="text-white hover:text-gray-300">
            {t('navbar.contact')}
          </Link>
        </div>

        {/* Right Section (Language Selector & Mobile Menu Button) */}
        <div className="flex-1 flex justify-end relative space-x-4">
          {/* Language Icon & Dropdown (Visible on Desktop) */}
          <div className="hidden md:block relative" ref={dropdownRef}>
            <button
              className="text-white text-xl focus:outline-none"
              onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
            >
              <LanguageIcon />
            </button>

            {/* Language Dropdown */}
            {isLanguageDropdownOpen && (
              <div className="absolute right-0 mt-2 w-32 bg-gray-800 text-white rounded-lg shadow-lg">
                <ul className="py-2 text-center">
                  <li
                    className="px-4 py-2 hover:bg-gray-700 cursor-pointer"
                    onClick={() => changeLanguage('en')}
                  >
                    EN
                  </li>
                  <li
                    className="px-4 py-2 hover:bg-gray-700 cursor-pointer"
                    onClick={() => changeLanguage('de')}
                  >
                    DE
                  </li>
                  <li
                    className="px-4 py-2 hover:bg-gray-700 cursor-pointer"
                    onClick={() => changeLanguage('it')}
                  >
                    IT
                  </li>
                  <li
                    className="px-4 py-2 hover:bg-gray-700 cursor-pointer"
                    onClick={() => changeLanguage('fr')}
                  >
                    FR
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            className="md:hidden text-white focus:outline-none"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu (Sliding in from the Right) */}
      <div
        className={`fixed top-0 right-0 w-full h-full bg-black flex flex-col items-center justify-center text-white z-50 transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <button
          className="absolute top-6 right-6 text-white text-2xl"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <CloseIcon />
        </button>

        <Link
          to="/"
          className="py-4 text-xl"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          {t('navbar.home')}
        </Link>
        <Link
          to="/about"
          className="py-4 text-xl"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          {t('navbar.about')}
        </Link>
        <Link
          to="/contact"
          className="py-4 text-xl"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          {t('navbar.contact')}
        </Link>

        {/* Language Options (Displayed in a Row on Mobile) */}
        <div className="mt-6 text-xl">
          <span
            className="px-3 cursor-pointer"
            onClick={() => changeLanguage('en')}
          >
            EN
          </span>{' '}
          |
          <span
            className="px-3 cursor-pointer"
            onClick={() => changeLanguage('de')}
          >
            DE
          </span>{' '}
          |
          <span
            className="px-3 cursor-pointer"
            onClick={() => changeLanguage('it')}
          >
            IT
          </span>{' '}
          |
          <span
            className="px-3 cursor-pointer"
            onClick={() => changeLanguage('fr')}
          >
            FR
          </span>
        </div>
      </div>
    </>
  );
};

export default Navbar;
